import Icons from "@/assets/icons";
import Button from "@/components/Button";
import Input from "@/components/Input";
import Select from "@/components/Select";
import { ApiService } from "@/services/ApiService";
import { messageService } from "@/services/MessageService";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { validateFormikFields } from "@/lib/utils";
import { useEffect, useState } from "react";
import RegisterClientSchema from "./Schema/RegisterClientSchema";

const formatCNPJ = (value: string) => {
  if (!value) return "";
  return value
    .replace(/\D/g, "")
    .slice(0, 14)
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};

const formatCEP = (value: string) => {
  if (!value) return "";
  return value
    .replace(/\D/g, "")
    .slice(0, 8)
    .replace(/^(\d{5})(\d)/, "$1-$2");
};

const formatTelefone = (value: string) => {
  if (!value) return "";
  return value
    .replace(/\D/g, "")
    .slice(0, 11)
    .replace(/^(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2");
};

const RegisterClient = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [schema, setSchema] = useState(RegisterClientSchema(t));
  const { post, patch, get } = ApiService;
  const navigate = useNavigate();

  const initialValues = {
    razaoSocial: "",
    nomeFantasia: "",
    cnpj: "",
    cep: "",
    endereco: "",
    telefoneResponsavel: "",
    emailResponsavel: "",
    status: "ATIVO",
  };

  const [apiData, setApiData] = useState<typeof initialValues>();

  const formikData = useFormik({
    initialValues,
    validate: (values) => {
      const cleanedValues = {
        ...values,
        cnpj: values.cnpj.replace(/\D/g, ""),
        cep: values.cep.replace(/\D/g, ""),
        telefoneResponsavel: values.telefoneResponsavel.replace(/\D/g, ""),
      };
      return validateFormikFields(cleanedValues, schema);
    },
    onSubmit: async (values) => {
      const payload = {
        ...values,
        cnpj: values.cnpj.replace(/\D/g, ""),
        cep: values.cep.replace(/\D/g, ""),
        telefoneResponsavel: values.telefoneResponsavel.replace(/\D/g, ""),
      };

      try {
        const request = id
            ? patch(`api/v1/cliente/atualizar/${id}`, payload)
            : post("api/v1/cliente/registrar", payload);

        const { status } = await request;

        if (status === 200) {
            const message = id
            ? {
                title: t("clientsForm.messages.update.title"),
                detail: t("clientsForm.messages.update.description"),
                }
            : {
                title: t("clientsForm.messages.save.title"),
                detail: t("clientsForm.messages.save.description"),
                };

            messageService.success(message.title, message.detail, () =>
            navigate("/gestao-clientes")
            );
        }
      } catch (error: any) {
        // Tratamento de erro específico para conflito de status (licença ativa)
        if (error.response?.status === 409) {
             messageService.error(
                 t("clientsForm.messages.error.title", "Erro"),
                 error.response.data?.message || error.message
             );
        } else {
             console.error(error);
        }
      }
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      const { data } = await get(`api/v1/cliente/buscarPorId/${id}`);
      setApiData(data);
    };

    if (id) {
      fetchData();
      setSchema(RegisterClientSchema(t));
    }
  }, [id]);

  useEffect(() => {
    if (apiData) {
      formikData.setValues({
        razaoSocial: apiData.razaoSocial ?? "",
        nomeFantasia: apiData.nomeFantasia ?? "",
        cnpj: formatCNPJ(apiData.cnpj) ?? "",
        cep: formatCEP(apiData.cep) ?? "",
        endereco: apiData.endereco ?? "",
        telefoneResponsavel: formatTelefone(apiData.telefoneResponsavel) ?? "",
        emailResponsavel: apiData.emailResponsavel ?? "",
        status: apiData.status ?? "ATIVO",
      });
    }
  }, [apiData]);

  const handleClearForm = () => {
    if (id) {
      const currentCnpj = formikData.values.cnpj;
      formikData.resetForm({
        values: {
          ...initialValues,
          cnpj: currentCnpj,
        },
      });
    } else {
      formikData.resetForm();
    }
  };

  const statusOptions = [
    { title: t("table.user.body.active.true", "Ativo"), value: "ATIVO" },
    { title: t("table.user.body.active.false", "Inativo"), value: "INATIVO" },
  ];

  // Estilos padronizados
  const inputsFieldsetStyle =
    "bg-white rounded-lg border p-6 shadow-sm flex flex-col gap-6";
  const titleFieldsetStyle =
    "text-xl font-semibold text-gray-800 border-b pb-4 mb-4";
  const buttonsFieldsetStyle =
    "flex max-md:flex-wrap col-span-full justify-end gap-4";

  return (
    <div className="flex-1 p-8 max-w-7xl mx-auto">
      <div className="mb-8 border-b pb-4">
        <h1 className="text-3xl font-medium text-gray-800">
          {id ? t("clientsForm.titleEdit") : t("clientsForm.title")}
        </h1>
      </div>

      <form onSubmit={formikData.handleSubmit} className="flex flex-col gap-8">
        <fieldset className={inputsFieldsetStyle}>
          <h2 className={titleFieldsetStyle}>{t("clientsForm.sections.main")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            
            <div className="md:col-span-6">
              <Input
                id="razaoSocial"
                label={t("clientsForm.inputs.razaoSocial.label")}
                error={
                  formikData.errors.razaoSocial &&
                  formikData.touched.razaoSocial
                    ? formikData.errors.razaoSocial
                    : ""
                }
                {...formikData.getFieldProps("razaoSocial")}
              />
            </div>
            
            <div className="md:col-span-6">
              <Input
                id="nomeFantasia"
                label={t("clientsForm.inputs.nomeFantasia.label")}
                error={
                  formikData.errors.nomeFantasia &&
                  formikData.touched.nomeFantasia
                    ? formikData.errors.nomeFantasia
                    : ""
                }
                {...formikData.getFieldProps("nomeFantasia")}
              />
            </div>

            <div className="md:col-span-4">
              <Input
                id="cnpj"
                label={t("clientsForm.inputs.cnpj.label")}
                error={
                  formikData.errors.cnpj && formikData.touched.cnpj
                    ? formikData.errors.cnpj
                    : ""
                }
                value={formikData.values.cnpj}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  formikData.setFieldValue("cnpj", formatCNPJ(e.target.value));
                }}
                disabled={!!id}
              />
            </div>

            <div className="md:col-span-4">
              <Input
                id="telefoneResponsavel"
                label={t("clientsForm.inputs.telefoneResponsavel.label")}
                error={
                  formikData.errors.telefoneResponsavel &&
                  formikData.touched.telefoneResponsavel
                    ? formikData.errors.telefoneResponsavel
                    : ""
                }
                value={formikData.values.telefoneResponsavel}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  formikData.setFieldValue(
                    "telefoneResponsavel",
                    formatTelefone(e.target.value)
                  );
                }}
              />
            </div>

            <div className="md:col-span-4">
               <Input
                id="emailResponsavel"
                label={t("clientsForm.inputs.emailResponsavel.label")}
                error={
                  formikData.errors.emailResponsavel &&
                  formikData.touched.emailResponsavel
                    ? formikData.errors.emailResponsavel
                    : ""
                }
                {...formikData.getFieldProps("emailResponsavel")}
              />
            </div>

            <div className="md:col-span-4">
              <Input
                id="cep"
                label={t("clientsForm.inputs.cep.label")}
                error={
                  formikData.errors.cep && formikData.touched.cep
                    ? formikData.errors.cep
                    : ""
                }
                value={formikData.values.cep}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  formikData.setFieldValue("cep", formatCEP(e.target.value));
                }}
              />
            </div>

            <div className="md:col-span-4">
              <Select
                id="status"
                label={t("table.user.head.active", "Status")}
                placeholder="Selecione o status"
                options={statusOptions}
                error={
                  formikData.errors.status && formikData.touched.status
                    ? (formikData.errors.status as string)
                    : ""
                }
                {...formikData.getFieldProps("status")}
              />
            </div>

            <div className="md:col-span-12">
              <Input
                id="endereco"
                label={t("clientsForm.inputs.endereco.label")}
                error={
                  formikData.errors.endereco && formikData.touched.endereco
                    ? formikData.errors.endereco
                    : ""
                }
                {...formikData.getFieldProps("endereco")}
              />
            </div>

          </div>
        </fieldset>

        <fieldset className={buttonsFieldsetStyle}>
          <Button
            label={t("clientsForm.buttons.clear")}
            color="neutro"
            onClick={handleClearForm}
            icon={<Icons.Delete pathStyle="fill-inherit" />}
            type="button"
          />

          <Button
            label={t("clientsForm.buttons.cancel")}
            color="neutro"
            onClick={() => navigate("/gestao-clientes")}
            icon={<Icons.Close pathStyle="fill-inherit" />}
            type="button"
          />

          <Button
            label={t("clientsForm.buttons.save")}
            color="primario"
            disabled={formikData.isSubmitting}
            icon={<Icons.Save pathStyle="fill-inherit" />}
            type="submit"
          />
        </fieldset>
      </form>
    </div>
  );
};

export default RegisterClient;